import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import jstz from 'jstimezonedetect';
import ApplicationRow from './ApplicationRow';
import IdentityProviderCard from './IdentityProviderCard';
import SortableHeader from '../Controls/SortableHeader';
import { bigNumberToText, getUserState, httpGet } from '../utils';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import {
    AUTH_TYPES_GRAPHS_COLORS,
    COLUMN_TYPES,
    DIRECTORY_TYPES,
    HTTP_OPTIONS,
    INFINITE_SCROLL_BATCH_SIZE,
    SORT_DIRECTION
} from '@silverfort-nodelibs/common/utils/consts';
import styles from './ApplicationsDiscovery.module.css';
import * as filterTypes from '../Controls/Filters/filterTypes';

import leftArrowIcon from '../common/Images/arrow_bullet_left.png';
import rightArrowIcon from '../common/Images/arrow_bullet.png';
import FiltersMenu from '../Controls/Filters/FiltersMenu.js';
import { APP_DISCOVERY_FILTERS } from '../Controls/Filters/FiltersObjects.js';

const ApplicationDiscovery = ({ user }) => {
    const [showLoader, setShowLoader] = useState(true);
    const [userData, setUserData] = useState({});
    const [applications, setApplications] = useState([]);
    const [totalAppsCount, setTotalAppsCount] = useState(0);
    const [identityProviders, setIdentityProviders] = useState([]);
    const [sortByHeaderId, setSortByHeaderId] = useState(0);
    const [sortDirection, setSortDirection] = useState(1);
    const [hasMoreData, setHasMoreData] = useState(false);
    const [paginationOffset, setPaginationOffset] = useState(0);
    const scrollRef = useRef(null);

    /// Filters
    const [filters, setFilters] = useState(APP_DISCOVERY_FILTERS);

    const columns = [
        {
            id: 0,
            header: 'Application Name',
            accesor: 'name',
            accesor_type: COLUMN_TYPES.STRING
        },
        {
            id: 1,
            header: 'Auth Type',
            accesor: 'auth_type',
            accesor_type: COLUMN_TYPES.STRING
        },
        {
            id: 2,
            header: 'Active Users',
            accesor: 'active_users',
            accesor_type: COLUMN_TYPES.INT
        },
        {
            id: 3,
            header: 'Sign-In Count',
            accesor: 'auths_count',
            accesor_type: COLUMN_TYPES.INT
        },
        {
            id: 4,
            header: 'Last Used',
            accesor: 'last_used',
            accesor_type: COLUMN_TYPES.INT
        }
    ];

    const sortByHeader = (columnId, sortDir) => {
        setPaginationOffset(0);
        setSortByHeaderId(columnId);
        setSortDirection(sortDir);
    };

    const headers = columns.map((column) => {
        return (
            <SortableHeader
                id={column.id}
                key={column.id}
                classNames={`${styles.sortableHeader}`}
                header={column.header}
                title={column.title}
                sorted={column.id === sortByHeaderId}
                reverseIconDirection={column.reverse_sort}
                sortByHeader={sortByHeader}
                tooltip={column.tooltip}
            />
        );
    });

    headers.push(<div key={columns.length + 1}></div>);

    const loadMoreData = () => {
        loadApplications(true);
    };

    const fetchInitialData = async () => {
        const response = await httpGet(`/ApplicationsDiscovery/discoveryStatistics`);
        if (response.data) {
            if (response.data.discoveryStatistics) {
                const filterdIDPs = {};
                Object.keys(response.data.discoveryStatistics).forEach((idpKey) => {
                    if (response.data.discoveryStatistics[idpKey].isConfigured) {
                        filterdIDPs[idpKey] = response.data.discoveryStatistics[idpKey];
                    }
                });
                setIdentityProviders(filterdIDPs);
            }

            const userData = await getUserState();
            setUserData(userData);
        }

        await loadApplications();
        setShowLoader(false);
    };

    const loadApplications = async (appendData) => {
        let orderDirection = sortDirection;

        if (
            sortByHeaderId !== -1 &&
            sortByHeaderId !== 4 &&
            (columns[sortByHeaderId].accesor_type === COLUMN_TYPES.BOOLEAN ||
                columns[sortByHeaderId].accesor_type === COLUMN_TYPES.INT)
        ) {
            orderDirection = orderDirection * -1;
        }
        const data = {
            filters: buildFilterValues(),
            localTimeZone: encodeURIComponent(jstz.determine().name()),
            paginationOffset: paginationOffset,
            orderByField: columns[sortByHeaderId].accesor,
            orderDirection: orderDirection === SORT_DIRECTION.ASC ? 'ASC' : 'DESC'
        };

        const response = await httpGet(`/ApplicationsDiscovery/discoveryApplications`, {
            [HTTP_OPTIONS.REQ_OPTIONS]: { params: data },
            [HTTP_OPTIONS.HANDLE_ERRORS]: true
        });
        if (response.data) {
            if (response.data.applications && response.data.applications.length > 0) {
                const newApplications = appendData ? [...applications] : [];
                newApplications.push(...response.data.applications);
                setPaginationOffset(response.data.paginationOffset);
                setApplications(newApplications);
                setTotalAppsCount(newApplications[0].total_count || 0);
                setHasMoreData(response.data.applications.length === INFINITE_SCROLL_BATCH_SIZE);
            } else {
                if (paginationOffset === 0) {
                    setApplications([]);
                    setTotalAppsCount(0);
                }
                setHasMoreData(false);
            }
        }
    };

    useEffect(() => {
        loadApplications();
    }, [filters, sortByHeaderId, sortDirection]);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const applicationsRows = applications.map((applicationData) => {
        return (
            <ApplicationRow
                key={`${applicationData.identifier}_${applicationData.auth_type}`}
                user={userData.user}
                name={applicationData.name}
                directoryName={applicationData.directoryName}
                guid={applicationData.identifier}
                authType={applicationData.auth_type}
                usersCount={applicationData.active_users}
                authsCount={applicationData.auths_count}
                lastUsed={applicationData.last_used}
                supportBridge={
                    identityProviders &&
                    identityProviders[applicationData.auth_type] &&
                    identityProviders[applicationData.auth_type].supportBridge
                }
                className={`${styles.applicationRow}`}
            />
        );
    });

    const identityProvidersRows = Object.values(identityProviders).map((applicationData) => {
        return (
            <IdentityProviderCard
                key={applicationData.key}
                name={applicationData.displayName}
                appsCount={applicationData.appsCount}
                appsHeader={applicationData.appsTitle}
                authsCount={applicationData.authsCount}
                policiesCount={applicationData.policies}
                bridgedAppsCount={applicationData.bridgePolicies}
                supportBridge={applicationData.supportBridge}
                connectorMissConfigured={applicationData.connectorMissConfigured}
                settingsSection={applicationData.settingsSection}
            />
        );
    });

    const filterValueChangedHandler = (filterId, newValue, isExcluded = false) => {
        const newFilters = { ...filters };
        newFilters[filterId] = { ...newFilters[filterId] };

        if (newFilters[filterId].filterType === filterTypes.EXTERNAL) {
            newFilters[filterId].value = newValue.objectEntities.map((entity) => {
                return { label: entity.name || entity.display_name, value: entity.guid };
            });
            newFilters[filterId].externalSelections = { entities: newValue.objectEntities };
        } else {
            newFilters[filterId].value = newValue !== null ? newValue : newFilters[filterId].defaultValue;
        }

        newFilters[filterId].isExcluded = isExcluded;
        setPaginationOffset(0);
        setFilters(newFilters);
    };

    const buildFilterValues = () => {
        const filterValues = {};

        for (const fId in filters) {
            const filter = filters[fId];
            if (
                Array.isArray(filter.value)
                    ? filter.value.length !== 0
                    : filter.value !== null && filter.value !== undefined
            ) {
                filterValues[fId] = { value: filter.value, isExcluded: filter.isExcluded };
            }
        }

        return filterValues;
    };

    const scroll = (scrollOffset) => {
        scrollRef.current.scrollLeft += scrollOffset;
    };

    return (
        <div id={styles.applicationsDiscoveryPageContainer}>
            {showLoader && (
                <div className="silverfort-loader">
                    <div className="logo"></div>
                </div>
            )}
            <div className={styles.container}>
                <div className={styles.idpsContainer}>
                    <div className={styles.identityProviders}>
                        <div className={styles.sectionHeader}>Identity Providers</div>
                        <div className={styles.scroller}>
                            <div className={`${styles.scrollButton} ${styles.scrollLeft}`} onClick={() => scroll(-200)}>
                                <img src={leftArrowIcon} alt="" />
                            </div>
                            <div className={styles.cardsContainer} ref={scrollRef}>
                                {identityProvidersRows}
                            </div>
                            <div className={`${styles.scrollButton} ${styles.scrollRight}`} onClick={() => scroll(200)}>
                                <img src={rightArrowIcon} alt="" />
                            </div>
                        </div>
                    </div>
                    <div></div>
                    <div className={styles.statistics}>
                        <div>
                            <div className={styles.sectionHeader}>Statistics</div>
                            <div className={styles.pieContainer}>
                                <div className={styles.chartArea}>
                                    <div className={styles.chartContainer}>
                                        <ResponsiveContainer width="100%">
                                            <PieChart>
                                                <Pie
                                                    data={Object.values(identityProviders)}
                                                    dataKey={'authsCount'}
                                                    innerRadius={'70%'}
                                                    outerRadius={'90%'}
                                                    fill="#8884d8"
                                                    paddingAngle={-1}>
                                                    {Object.values(identityProviders).map((entry) => (
                                                        <Cell
                                                            key={`cell-${entry.key}`}
                                                            fill={AUTH_TYPES_GRAPHS_COLORS[entry.key]}
                                                            stroke=""
                                                        />
                                                    ))}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className={styles.legend}>
                                        <div className={styles.title}>Authentications Distribution</div>
                                        <div className={styles.legendParts}>
                                            {Object.values(identityProviders).map((authTypeStats) => {
                                                return (
                                                    <div key={authTypeStats.key} className={styles.legendField}>
                                                        <span className={styles[authTypeStats.key]}>
                                                            {bigNumberToText(authTypeStats.authsCount || 0)}
                                                        </span>
                                                        <div>{DIRECTORY_TYPES[authTypeStats.key].displayName}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.sectionHeader}>
                    <div>
                        Applications Table<span className={styles.appsCount}>{`(${totalAppsCount})`}</span>
                    </div>
                </div>
                <div className={styles.applicationsTable}>
                    <FiltersMenu filters={Object.values(filters)} onValueChange={filterValueChangedHandler} />
                    <div className={`${styles.applicationRow}  ${styles.header}`}>{headers}</div>
                    <div className="hidden">{sortByHeaderId}</div>
                    <div className="hidden">{sortDirection}</div>
                    <div className={`${styles.applicationsContainer} dark-scrollbar`}>
                        <InfiniteScroll
                            pageStart={0}
                            loadMore={loadMoreData}
                            threshold={100}
                            hasMore={hasMoreData}
                            initialLoad={false}
                            useWindow={false}
                            loader={<div key={0} className="dataLoader"></div>}>
                            {applicationsRows}
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicationDiscovery;
